import { addressesMatch } from '@/lib/address-match';
import type { AddressFieldsValue } from '@/components/molecules/AddressFields';
import type { LeadServiceAddressInput } from '@/types/lead';

/**
 * NOT-586 — what the lead sheet should do with its optional service address on
 * save, decided in one place because the sheet was asking one comparison two
 * different questions.
 *
 * The two questions are NOT the same, and conflating them costs a real edit:
 *
 *   1. "Did the user change anything?" — the persist decision. This is a RAW
 *      field comparison. Correcting '123 main st' to '123 Main St', or
 *      'V3M0P2' to 'V3M 0P2', is an edit the user made deliberately and expects
 *      to see reflected. NOT-586 routed this through the normalized comparator,
 *      which by design calls those two spellings equal — so the save succeeded
 *      and the displayed address never changed.
 *
 *   2. "Does the draft still name the same place the lead is already linked
 *      to?" — the duplicate-row question NOT-478 asked. It has to be the
 *      NORMALIZED comparison, because `POST /leads/:id { serviceAddress }`
 *      always INSERTS a new ContactAddress (leads.service.ts
 *      `createLeadServiceAddress`). Re-posting a same-place address leaves the
 *      contact holding two rows for one address.
 *
 * Answering both separately is what lets a formatting-only correction persist
 * WITHOUT a duplicate row: it rewrites the ContactAddress the lead already
 * points at, in place, instead of inserting a second one.
 */
export type LeadServiceAddressAction =
  /** Nothing to save — the draft is the untouched seed, or was never filled in. */
  | { kind: 'none' }
  /** The user emptied the fields: drop the lead's link to its address. */
  | { kind: 'unlink' }
  /** A different place: the backend inserts a row and relinks the lead. */
  | { kind: 'create'; address: LeadServiceAddressInput }
  /**
   * The same place, respelled. Rewrite the linked row rather than inserting a
   * near-identical one; the lead's `serviceAddressId` is already correct.
   */
  | { kind: 'rewrite'; addressId: string; address: LeadServiceAddressInput };

/** The four parts the backend requires before it will accept an address. */
export function isLeadAddressComplete(address: AddressFieldsValue): boolean {
  return Boolean(
    address.street1.trim() &&
    address.city.trim() &&
    address.state.trim() &&
    address.postalCode.trim(),
  );
}

/** Whether any of the place fields carry text — a half-filled draft counts. */
export function leadAddressHasInput(address: AddressFieldsValue): boolean {
  return Boolean(
    address.street1.trim() ||
    address.city.trim() ||
    address.state.trim() ||
    address.postalCode.trim(),
  );
}

/**
 * Field-for-field equality on the values as they would be SENT.
 *
 * Trimmed, because trailing whitespace does not survive the payload and a save
 * that differs only there would post a byte-identical duplicate. Otherwise
 * exact: case and internal spacing are the user's edit, not noise, which is
 * precisely where this parts company with `addressesMatch`.
 */
function rawFieldsEqual(left: AddressFieldsValue, right: AddressFieldsValue): boolean {
  return (
    left.street1.trim() === right.street1.trim() &&
    left.street2.trim() === right.street2.trim() &&
    left.city.trim() === right.city.trim() &&
    left.state.trim() === right.state.trim() &&
    left.postalCode.trim() === right.postalCode.trim() &&
    (left.country || 'US') === (right.country || 'US')
  );
}

function toPayload(address: AddressFieldsValue): LeadServiceAddressInput {
  return {
    street1: address.street1.trim(),
    street2: address.street2.trim() || undefined,
    city: address.city.trim(),
    state: address.state.trim(),
    postalCode: address.postalCode.trim(),
    country: address.country || 'US',
  };
}

export function resolveLeadServiceAddressAction(args: {
  /** The address as it stands in the form. */
  draft: AddressFieldsValue;
  /** The address as the sheet first loaded it — empty when creating. */
  initial: AddressFieldsValue;
  isEdit: boolean;
  /** The ContactAddress the lead currently points at, if any. */
  serviceAddressId?: string | null;
}): LeadServiceAddressAction {
  const { draft, initial, isEdit, serviceAddressId } = args;
  const complete = isLeadAddressComplete(draft);

  // Creating: there is no seeded address to duplicate, so a complete address is
  // simply sent. An incomplete one is the caller's problem (it blocks the save).
  if (!isEdit) return complete ? { kind: 'create', address: toPayload(draft) } : { kind: 'none' };

  // Question 1 — did the user touch it? Raw, so a respelling counts.
  if (rawFieldsEqual(draft, initial)) return { kind: 'none' };

  if (!complete) {
    // Every field emptied is an instruction to unlink. A half-filled one is not
    // an instruction at all: LeadFormSheet blocks the save before calling this,
    // but this module is exported and pure, so it answers rather than assumes —
    // `none` leaves the existing link alone instead of unlinking on a draft the
    // user is still typing.
    return leadAddressHasInput(draft) ? { kind: 'none' } : { kind: 'unlink' };
  }

  // Question 2 — same place, respelled? Then rewrite the row the lead already
  // points at. `compareCountry` is on because both operands are this form's own
  // state: an absent country here is a real absence, not a persistence stamp.
  const namesSamePlace = addressesMatch(draft, initial, { compareCountry: true });
  if (namesSamePlace && serviceAddressId) {
    return { kind: 'rewrite', addressId: serviceAddressId, address: toPayload(draft) };
  }

  return { kind: 'create', address: toPayload(draft) };
}
